import { XMarkIcon } from '@heroicons/react/24/outline';
import { Column, Row } from '@tanstack/react-table';
import { useState } from 'react';

import { IconButton } from '@/components/core/IconButton';
import { LOCAL_STORAGE } from '@/constants.ts';
import ConfirmationModal from '@/dialogs/confirmation-modals/ConfirmationModal';
import { useLocalStorage } from '@/hooks/useLocalStorage.ts';
import { useTranslation } from '@/hooks/useTranslation';
import { useUser } from '@/hooks/useUser';

interface MultiReferenceActionCell<TData> {
    row: Row<TData>;
    column: Column<TData>;
    remove: (rowIndex: number) => void,
}

const MultiReferenceActionCell = <TData,>({ remove, row }: MultiReferenceActionCell<TData>) => {
    const { t } = useTranslation();
    const { user } = useUser();
    const [showRemoveDialog, setShowRemoveDialog] = useState(false);

    const [isDeleteConfirmationDisabled] =
        useLocalStorage<boolean>(
            `${LOCAL_STORAGE.DISABLE_DELETE_CONFIRMATION}${user?.id}`,
            false,
        );

    const handleRemove = (closeDialog = true) => {
        remove(row.index);
        if (closeDialog) setShowRemoveDialog(false);
    };

    const handleCancel = () => {
        setShowRemoveDialog(false);
    };

    return (
        <div
            id={`row${row.id}-actions`}
            className="flex items-center justify-start h-full min-h-[40px] pl-2"
        >
            <IconButton
                type="button"
                data-testid={`delete-entry-${row.id}`}
                className='tw-icon-primary'
                icon={XMarkIcon}
                variant="destructive"
                onClick={() => isDeleteConfirmationDisabled? handleRemove(false): setShowRemoveDialog(true)}
                title={t('MULTI_REFERENCE_DETACH_UNIT_TOOLTIP')}
                size='md'
            />
            <ConfirmationModal
                title='MULTI_REFERENCE_DETACH_UNIT.TITLE'
                subTitle='MULTI_REFERENCE_DETACH_UNIT.SUBTITLE_1'
                discardBtnText = 'MULTI_REFERENCE_DETACH_UNIT.REMOVE'
                closeBtnText ='CANCEL'
                btnVariant="destructive"
                show={showRemoveDialog}
                onSubmit={handleRemove}
                onClose={handleCancel}
            />
        </div>
    );
};

export { MultiReferenceActionCell };
